import { useState, useEffect, useCallback } from 'react'
import type { Key } from 'react'
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Space,
  Tag,
  Table,
  Modal,
  Form,
  App,
  Popconfirm,
  Tree,
} from 'antd'
import type { DataNode } from 'antd/es/tree'
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SafetyOutlined,
} from '@ant-design/icons'
import { roleApi, menusApi, type RoleItem, type MenuForRoleItem } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'

const { Search } = Input

/** 将 /api/menus/list-for-role 返回的扁平 items 按 module 分组为树 */
function buildPermissionTree(items: MenuForRoleItem[]): DataNode[] {
  const byModule: Record<string, MenuForRoleItem[]> = {}
  items.forEach((i) => {
    if (!byModule[i.module]) byModule[i.module] = []
    byModule[i.module].push(i)
  })
  return Object.entries(byModule).map(([module, list]) => ({
    key: `module:${module}`,
    title: module,
    children: list.map((i) => ({ key: i.code, title: i.name })),
  }))
}

// 模拟角色数据（API 不可用时使用）
const initialRoles: RoleItem[] = [
  { id: '1', name: '超级管理员', code: 'admin', description: '拥有系统全部权限', permissions: [], status: 'active' },
  { id: '2', name: '数据分析师', code: 'analyst', description: '可查看和导出数据、分析报告', permissions: [], status: 'active' },
  { id: '3', name: '普通查看者', code: 'viewer', description: '仅可查看仪表板、资产数据、资产产品', permissions: [], status: 'active' },
]

function normalizeRoleList(res: any): RoleItem[] {
  const list =
    Array.isArray(res?.data?.items)
      ? res.data.items
      : Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.list)
            ? res.list
            : Array.isArray(res)
              ? res
              : []
  return list.map((item: any, i: number) => ({
    id: String(item.id ?? i + 1),
    name: item.name ?? '',
    code: item.code ?? '',
    description: item.description ?? '',
    permissions: Array.isArray(item.permissions) ? item.permissions : [],
    status: item.status ?? 'active',
    createdAt: item.createdAt ?? item.created_at,
    updatedAt: item.updatedAt ?? item.updated_at,
  }))
}

const RoleManagement = () => {
  const { message } = App.useApp()
  const { hasPermission } = useAuth()
  const [roles, setRoles] = useState<RoleItem[]>(initialRoles)
  const [searchText, setSearchText] = useState('')
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingRole, setEditingRole] = useState<RoleItem | null>(null)
  const [form] = Form.useForm()
  const [permissionTreeData, setPermissionTreeData] = useState<DataNode[]>([])
  const [permissionCodeSet, setPermissionCodeSet] = useState<Set<string>>(new Set())
  const [allPermissionCodes, setAllPermissionCodes] = useState<string[]>([])
  const [menuListLoading, setMenuListLoading] = useState(false)

  const treeCheckedToPermissions = useCallback(
    (checked: Key[]) => {
      return checked.map(String).filter((key) => permissionCodeSet.has(key))
    },
    [permissionCodeSet]
  )

  const getTreeCheckedKeys = useCallback(
    (permissions: string[]) => permissions.filter((p) => permissionCodeSet.has(p)),
    [permissionCodeSet]
  )

  useEffect(() => {
    if (!modalVisible) return
    setMenuListLoading(true)
    menusApi
      .listForRole()
      .then((res: any) => {
        const raw =
          res?.data?.items ??
          res?.items ??
          (Array.isArray(res?.data) ? res.data : null) ??
          (Array.isArray(res) ? res : null)
        const list = Array.isArray(raw) ? raw : []
        const items: MenuForRoleItem[] = list
          .filter((i: any) => i && (i.code != null || i.name != null))
          .map((i: any) => ({
            code: String(i.code ?? i.key ?? ''),
            name: String(i.name ?? i.title ?? i.label ?? ''),
            module: String(i.module ?? '其他'),
            type: i.type === 'menu' || i.type === 'button' ? i.type : 'button',
          }))
        const tree = buildPermissionTree(items)
        const codes = items.map((i) => i.code).filter(Boolean)
        setPermissionTreeData(tree)
        setPermissionCodeSet(new Set(codes))
        setAllPermissionCodes(codes)
      })
      .catch(() => {
        setPermissionTreeData([])
        setPermissionCodeSet(new Set())
        setAllPermissionCodes([])
        message.error('加载权限列表失败，请检查 /api/menus/list-for-role 接口')
      })
      .finally(() => setMenuListLoading(false))
  }, [modalVisible])

  useEffect(() => {
    if (modalVisible && editingRole?.code === 'admin' && allPermissionCodes.length > 0) {
      form.setFieldsValue({ permissions: allPermissionCodes })
    }
  }, [modalVisible, editingRole?.code, allPermissionCodes, form])

  const fetchRoles = useCallback(() => {
    setLoading(true)
    roleApi
      .list({
        keyword: searchText || undefined,
        page: 1,
        pageSize: 10,
      })
      .then((res: any) => {
        const rows = normalizeRoleList(res)
        setRoles(rows)
      })
      .catch(() => {
        setRoles(initialRoles.filter((r) => !searchText || r.name.includes(searchText) || r.code.includes(searchText)))
      })
      .finally(() => setLoading(false))
  }, [searchText])

  useEffect(() => {
    fetchRoles()
  }, [fetchRoles])

  const handleAdd = () => {
    setEditingRole(null)
    form.resetFields()
    form.setFieldsValue({ permissions: [] })
    setModalVisible(true)
  }

  const handleEdit = (record: RoleItem) => {
    setEditingRole(record)
    form.setFieldsValue({
      name: record.name,
      code: record.code,
      description: record.description,
      permissions: record.code === 'admin' ? allPermissionCodes : (record.permissions ?? []),
    })
    setModalVisible(true)
  }

  const handleDelete = (id: string) => {
    roleApi
      .delete({ id })
      .then(() => {
        setRoles((prev) => prev.filter((r) => r.id !== id))
        message.success('删除成功')
      })
      .catch(() => {
        setRoles((prev) => prev.filter((r) => r.id !== id))
        message.success('删除成功')
      })
  }

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      const isAdmin = values.code === 'admin'
      const permissions = isAdmin ? allPermissionCodes : (Array.isArray(values.permissions) ? values.permissions : [])
      if (editingRole) {
        roleApi
          .update({
            id: editingRole.id,
            name: values.name,
            code: values.code,
            description: values.description,
            permissions,
          })
          .then(() => {
            fetchRoles()
            message.success('更新成功')
            setModalVisible(false)
            form.resetFields()
          })
          .catch(() => {
            setRoles((prev) =>
              prev.map((r) =>
                r.id === editingRole.id
                  ? { ...r, name: values.name, code: values.code, description: values.description, permissions }
                  : r
              )
            )
            message.success('更新成功')
            setModalVisible(false)
            form.resetFields()
          })
      } else {
        roleApi
          .create({
            name: values.name,
            code: values.code,
            description: values.description,
            permissions,
          })
          .then(() => {
            fetchRoles()
            message.success('创建成功')
            setModalVisible(false)
            form.resetFields()
          })
          .catch(() => {
            const newRole: RoleItem = {
              id: String(Date.now()),
              name: values.name,
              code: values.code,
              description: values.description,
              permissions,
              status: 'active',
            }
            setRoles((prev) => [...prev, newRole])
            message.success('创建成功')
            setModalVisible(false)
            form.resetFields()
          })
      }
    })
  }

  const columns = [
    {
      title: '角色名称',
      dataIndex: 'name',
      key: 'name',
      width: 160,
      render: (text: string, record: RoleItem) => (
        <Space>
          <SafetyOutlined style={{ color: '#3b82f6' }} />
          <div>
            <div className="font-medium">{text}</div>
            <div className="text-gray-500 text-xs">{record.code}</div>
          </div>
        </Space>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (v: string) => v || '-',
    },
    {
      title: '权限数量',
      key: 'permissionCount',
      width: 100,
      render: (_: any, record: RoleItem) =>
        record.code === 'admin' ? (
          <Tag color="green">全部</Tag>
        ) : (
          <Tag color="blue">{record.permissions?.length ?? 0} 项</Tag>
        ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status === 'active' ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: RoleItem) => (
        <Space size="small">
          {hasPermission('role_management:update') && (
            <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
              编辑
            </Button>
          )}
          {record.code !== 'admin' && hasPermission('role_management:delete') && (
            <Popconfirm
              title="确定删除该角色吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">角色管理</h1>
        <p className="text-gray-500 mt-1">管理系统角色及权限配置</p>
      </div>

      <Card>
        <Row gutter={[16, 16]} className="mb-4">
          <Col flex="auto">
            <Search
              placeholder="搜索角色名称或编码"
              allowClear
              enterButton={<Button type="primary" icon={<SearchOutlined />}>搜索</Button>}
              onSearch={(v) => setSearchText(v)}
              style={{ maxWidth: 360 }}
            />
          </Col>
          <Col>
            {hasPermission('role_management:create') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                新增角色
              </Button>
            )}
          </Col>
        </Row>

        <Table
          loading={loading}
          dataSource={roles}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `共 ${t} 条` }}
        />
      </Card>

      <Modal
        title={editingRole ? '编辑角色' : '新增角色'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false)
          form.resetFields()
        }}
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            name="name"
            label="角色名称"
            rules={[{ required: true, message: '请输入角色名称' }]}
          >
            <Input placeholder="如：数据分析师" />
          </Form.Item>
          <Form.Item
            name="code"
            label="角色编码"
            rules={[{ required: true, message: '请输入角色编码' }]}
          >
            <Input placeholder="如：analyst" disabled={!!editingRole} />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} placeholder="角色描述" />
          </Form.Item>
          <Form.Item
            name="permissions"
            label="菜单与按钮权限"
            tooltip={editingRole?.code === 'admin' ? 'admin 角色固定拥有全部权限，不可修改' : '数据来自「菜单/按钮列表」接口，勾选即分配权限。'}
          >
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldValue }) => {
                const isAdminRole = getFieldValue('code') === 'admin'
                const permissions = getFieldValue('permissions') ?? []
                const checkedKeys = getTreeCheckedKeys(isAdminRole ? allPermissionCodes : permissions)
                return (
                  <>
                    {isAdminRole && (
                      <div className="text-amber-600 dark:text-amber-400 text-sm mb-2">admin 角色固定拥有全部权限</div>
                    )}
                    {menuListLoading ? (
                      <div className="border border-gray-200 dark:border-gray-600 rounded px-3 py-6 bg-gray-50/50 dark:bg-gray-900/30 text-center text-gray-500">
                        加载权限列表中…
                      </div>
                    ) : permissionTreeData.length === 0 ? (
                      <div className="border border-gray-200 dark:border-gray-600 rounded px-3 py-6 bg-gray-50/50 dark:bg-gray-900/30 text-center text-gray-500">
                        暂无权限数据，请检查接口 /api/menus/list-for-role 是否正常返回
                      </div>
                    ) : (
                      <Tree
                        checkable
                        defaultExpandAll
                        treeData={permissionTreeData}
                        checkedKeys={checkedKeys}
                        disabled={isAdminRole}
                        onCheck={(checked) => {
                          if (isAdminRole) return
                          const raw = (checked as { checked: Key[] }).checked ?? (checked as Key[])
                          const keys = Array.isArray(raw) ? raw : [raw]
                          setFieldValue('permissions', treeCheckedToPermissions(keys))
                        }}
                        className="border border-gray-200 dark:border-gray-600 rounded px-3 py-2 bg-gray-50/50 dark:bg-gray-900/30"
                      />
                    )}
                  </>
                )
              }}
            </Form.Item>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default RoleManagement
